// src/utils/phoneFormatter.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import countriesProfiles from 'assets/data/countriesProfiles.json';
import { applyNumberMask, returnOnlyNumbers } from 'utils/helpers';
import { detectBrazilianPhoneProfile } from 'utils/phone';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const FALLBACK_PHONE_MASKS = {
  landline: '(99) 9999-9999',
  mobile: '(99) 9.9999-9999',
  free: '9999-999-9999',
  robot: '999 999-9999',
};

// Fallback for countries without masks (7-15 digits, no mask)
const FALLBACK_INTERNATIONAL_MASK = '999999999999999'; // 15 digits

// *******************************************************************************
// FUNCTIONS
// *******************************************************************************
const getCountryProfile = (countryCode = 'BR') =>
  countriesProfiles?.find(c => c.countryCode === countryCode) || null;

const getPhoneMasks = (countryCode = 'BR') => {
  const c = getCountryProfile(countryCode);
  const masks = c?.phoneMasks;

  // Se não há máscaras ou estão vazias, retorna sem máscara (7-15 dígitos)
  if (!masks || !masks.landline || !masks.mobile) {
    return {
      landline: FALLBACK_INTERNATIONAL_MASK,
      mobile: FALLBACK_INTERNATIONAL_MASK,
      free: FALLBACK_INTERNATIONAL_MASK,
      robot: FALLBACK_INTERNATIONAL_MASK,
    };
  }

  return masks;
};

const normalizeProfileKey = key => (key || '').toString().trim().toLowerCase();

// detecções para outros países (mantém compatibilidade)
const isRobot = pn => pn?.startsWith('303') || pn?.startsWith('400');

// BR - Agora usa as funções do phone.js
const isFreeBR = pn => pn?.startsWith('0800') || pn?.startsWith('0300');
const isMobileBR = pn => pn?.length === 11 && pn?.substring(2, 3) === '9';

// US
const isFreeUS = pn =>
  ['800', '888', '877', '866', '855', '844', '833', '822'].includes(pn?.substring(0, 3));

// ES
const isFreeES = pn => pn?.startsWith('900') || pn?.startsWith('800');
const isMobileES = pn => pn?.length === 9 && (pn?.startsWith('6') || pn?.startsWith('7'));

// FR
const isFreeFR = pn => pn?.startsWith('080');
const isMobileFR = pn => pn?.length === 10 && (pn?.startsWith('06') || pn?.startsWith('07'));

// CN
const isFreeCN = pn => pn?.startsWith('800') || pn?.startsWith('400');
const isMobileCN = pn => pn?.length === 11 && pn?.startsWith('1');

// JP
const isFreeJP = pn => pn?.startsWith('0120');
const isMobileJP = pn => pn?.length === 11 && /^0(70|80|90)/.test(pn);

const chooseMask = (countryCode, profileKey) => {
  const masks = getPhoneMasks(countryCode);
  return masks?.[profileKey] ? masks[profileKey] : masks?.mobile || FALLBACK_INTERNATIONAL_MASK;
};

const applyMaskWithTrim = (digits, maskPattern) => {
  // Conta quantos placeholders (9 ou #) existem na máscara
  const placeholderCount = (maskPattern.match(/[9#]/g) || []).length;
  const maxLen = placeholderCount > 0 ? placeholderCount : 15; // fallback para 15 se não houver placeholders
  const sliced = (digits || '').slice(0, maxLen);

  // Se a máscara é o fallback internacional (15 dígitos sem formatação), retorna apenas os dígitos
  if (maskPattern === FALLBACK_INTERNATIONAL_MASK) {
    return {
      formatted: sliced,
      maxLength: maxLen,
    };
  }

  return {
    formatted: applyNumberMask(sliced, maskPattern, true),
    maxLength: maxLen,
  };
};

const detectPhoneProfile = (raw, countryCode = 'BR', profileOverride) => {
  const override = normalizeProfileKey(profileOverride);
  if (override) return override;

  const pn = returnOnlyNumbers(raw || '');
  const cc = (countryCode || 'BR').toUpperCase();

  // Para Brasil, usa a função unificada do phone.js
  if (cc === 'BR') {
    const profile = detectBrazilianPhoneProfile(pn);
    return profile || 'mobile'; // fallback para mobile se não detectar
  }

  // Para outros países, mantém a lógica existente
  if (isRobot(pn)) return 'robot';

  switch (cc) {
    case 'US':
      if (isFreeUS(pn)) return 'free';
      return 'landline';
    case 'es':
      if (isFreeES(pn)) return 'free';
      if (isMobileES(pn)) return 'mobile';
      return 'landline';
    case 'fr':
      if (isFreeFR(pn)) return 'free';
      if (isMobileFR(pn)) return 'mobile';
      return 'landline';
    case 'cn':
      if (isFreeCN(pn)) return 'free';
      if (isMobileCN(pn)) return 'mobile';
      return 'landline';
    case 'jp':
      if (isFreeJP(pn)) return 'free';
      if (isMobileJP(pn)) return 'mobile';
      return 'landline';
    default: {
      const masks = getPhoneMasks(cc);
      const landlineLen = (masks?.landline?.match(/[9#]/g) || []).length || 15;
      return pn?.length && pn?.length <= landlineLen ? 'landline' : 'mobile';
    }
  }
};

// *******************************************************************************
// MAIN EXPORTS
// *******************************************************************************
export const formatPhoneNumberByCountry = (rawValue, { countryCode, profile } = {}) => {
  const numeric = returnOnlyNumbers(rawValue || '');

  // Se não há countryCode, retornar apenas os dígitos sem formatação
  if (!countryCode) {
    return {
      formatted: numeric,
      profile: undefined,
      countryCode: undefined,
      maxLength: 15, // máximo internacional
      dial: undefined,
      flag: undefined,
    };
  }

  const cc = countryCode.toLowerCase();
  const profileKey = detectPhoneProfile(numeric, cc, profile);
  const maskPattern = chooseMask(cc, profileKey);
  const { formatted, maxLength } = applyMaskWithTrim(numeric, maskPattern);

  return {
    formatted,
    profile: profileKey,
    countryCode: cc,
    maxLength,
    dial: getCountryProfile(cc)?.dial,
    flag: getCountryProfile(cc)?.flag,
  };
};

export default {
  formatPhoneNumberByCountry,
};
